import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";

/**
 * Razorpay redirect-flow failure handler.
 * Razorpay redirects here when payment.failed fires before checkout closes,
 * OR when our Orders.jsx onfailure handler explicitly forwards the user here
 * with error_code + error_description query params.
 */
export default function PaymentFailed() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const errorCode = params.get("error_code") || "PAYMENT_FAILED";
  const errorDescription =
    params.get("error_description") ||
    "Your payment could not be completed. No charge was made to your account.";

  const friendlyMessages = {
    PAYMENT_CANCELLED: "Payment was cancelled. No charges have been made.",
    BAD_REQUEST_ERROR: "There was an issue with the payment request. Please try again.",
    GATEWAY_ERROR: "The payment gateway returned an error. Please try a different payment method.",
    SERVER_ERROR: "Our payment partner is temporarily unavailable. Please try again in a moment.",
  };
  const friendly = friendlyMessages[errorCode] || errorDescription;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white p-4">
      <div
        className="w-full max-w-md bg-white rounded-2xl border border-red-200 shadow-lg p-8 flex flex-col items-center"
        data-testid="payment-failed-page"
      >
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-5">
          <AlertTriangle className="h-8 w-8 text-red-600" />
        </div>

        <h2 className="font-['Outfit'] text-xl font-semibold text-red-700 mb-2 text-center">
          Payment Failed
        </h2>
        <p className="text-sm text-slate-600 text-center mb-1">{friendly}</p>
        <p className="text-[10px] text-slate-400 mt-1 mb-5 font-mono">[{errorCode}]</p>

        <div className="w-full bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-5">
          <p className="text-xs text-amber-800 leading-relaxed">
            <b>Don't worry</b> — no order was created and no money was charged. You can safely retry the booking.
          </p>
        </div>

        <div className="flex gap-3 w-full">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="flex-1 rounded-xl h-11 border-slate-200"
            data-testid="payment-failed-back-btn"
          >
            <ArrowRight className="h-4 w-4 mr-1.5 rotate-180" /> Back
          </Button>
          <Button
            onClick={() => navigate("/dashboard/orders")}
            className="flex-1 bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl h-11"
            data-testid="payment-failed-retry-btn"
          >
            <RotateCcw className="h-4 w-4 mr-1.5" /> Try Again
          </Button>
        </div>

        <p className="text-[11px] text-slate-400 mt-5 text-center">
          Need help? Email{" "}
          <a href="mailto:support@pickngo.co.in" className="text-[#FF6B00] hover:underline">support@pickngo.co.in</a>
        </p>
      </div>
    </div>
  );
}
